import { format, parseISO } from 'date-fns';
import { T } from 'gt-react';
import { CheckCircleIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';

/**
 * Presentation primitives shared by the short-pay and over-payment thread
 * summary cards. The two cards are deliberate twins — keeping the stat tile,
 * the detail bullet, and the formatters in one place stops them drifting apart
 * (they previously carried byte-identical copies of all of this).
 */

/** Reads a metadata value that should be a string-ish scalar. */
export function readString(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number') return String(value);
  return null;
}

/**
 * Human-readable money. Returns an em dash — never `$0.00` — for an unknown
 * amount, so a missing figure can't be mistaken for a zero balance.
 */
export function formatMoney(raw: string | null): string {
  if (raw == null) return '—';
  const num = Number(raw);
  if (Number.isNaN(num)) return raw;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

export function formatDate(raw: string | null): string | null {
  if (!raw) return null;
  const parsed = parseISO(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return format(parsed, 'MMM d, yyyy');
}

export type StatTone = 'warn' | 'pending' | 'credit';

/** One figure in a summary card's headline grid. */
export function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: React.ReactNode;
  /** A node rather than a string so a card can render "Not yet known" in words. */
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: StatTone;
}) {
  const toneClass =
    tone === 'warn'
      ? 'text-amber-700'
      : tone === 'pending'
        ? 'text-sky-700 dark:text-sky-400'
        : tone === 'credit'
          ? 'text-emerald-700'
          : 'text-foreground';
  return (
    <div className="px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={`text-xl font-semibold tabular-nums tracking-tight mt-1 ${toneClass}`}
      >
        {value}
      </div>
      {sub && (
        <div className="text-xs text-muted-foreground mt-0.5 truncate">
          {sub}
        </div>
      )}
    </div>
  );
}

/**
 * Resolved marker for a summary card's header strip.
 *
 * The page header carries the same status, but a collector reading the money
 * grid should not have to look back up to learn the thread is closed — the card
 * is where "is this still mine to chase?" gets asked. Styled to match the
 * header's status pill so the two read as one fact, not two.
 */
export function ResolvedBadge() {
  return (
    <Badge
      variant="outline"
      className="border-green-600 text-green-700 rounded-full"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-green-600 mr-1.5" />
      <T context="status badge on a collection thread summary card, the thread is resolved">
        Resolved
      </T>
    </Badge>
  );
}

export function DetailLine({ text }: { text: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-sm text-foreground/80 leading-snug">
      <CheckCircleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-muted-foreground/60" />
      <span>{text}</span>
    </li>
  );
}
